import { memberId } from '@trello/session-cookie';
import { useCallback, useMemo, useState } from 'react';
import { useAddMessageDismissedMutation } from './AddMessageDismissedMutation.generated';
import { useMyMessagesDismissedQuery } from './MyMessagesDismissedQuery.generated';

export const useMessagesDismissed = () => {
  const [
    optimisticallyDismissedMessages,
    setOptimisticallyDismissedMessages,
  ] = useState<string[]>([]);
  const { data } = useMyMessagesDismissedQuery({
    variables: { idMember: memberId || 'me' },
    fetchPolicy: 'cache-only',
    waitOn: ['MemberHeader'],
  });
  const [addMessageDismissed] = useAddMessageDismissedMutation();

  const messagesDismissed = useMemo(
    () => data?.member?.messagesDismissed ?? [],
    [data?.member?.messagesDismissed],
  );

  const dismissMessage = useCallback(
    async (
      name: string,
      options: { optimistic?: boolean } = { optimistic: true },
    ) => {
      try {
        if (options.optimistic) {
          setOptimisticallyDismissedMessages(
            optimisticallyDismissedMessages.concat([name]),
          );
        }

        await addMessageDismissed({
          variables: {
            memberId: memberId || 'me',
            name,
            lastDismissed: new Date().toISOString(),
          },
        });
      } finally {
        setOptimisticallyDismissedMessages(
          optimisticallyDismissedMessages.filter(
            (messageName) => messageName !== name,
          ),
        );
      }
    },
    [
      addMessageDismissed,
      setOptimisticallyDismissedMessages,
      optimisticallyDismissedMessages,
    ],
  );

  const isMessageDismissed = useCallback(
    (name: string) =>
      messagesDismissed.some((message) => message.name === name) ||
      optimisticallyDismissedMessages.some(
        (messageName) => messageName === name,
      ),
    [messagesDismissed, optimisticallyDismissedMessages],
  );

  return {
    messagesDismissed,
    dismissMessage,
    isMessageDismissed,
  };
};
